import { AnimatePresence } from "framer-motion";
import { compact, groupBy, range } from "lodash";
import moment from "moment";
import { forwardRef, useEffect, useState } from "react";
import {
  MeetingDialogFragmentFragment,
  MeetingsOverviewMeetingFragment,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import MotionDiv from "@components/motion/motion-div";
import { classNames } from "@helpers/css";
import { assertEdgesNonNullWithStringId } from "@helpers/helpers";

import OverviewMeeting from "./overview-meeting";

enum MeetingType {
  ALL,
  ONE_ON_ONE,
  GROUP,
}

type Props = {
  meetings: (MeetingDialogFragmentFragment | MeetingsOverviewMeetingFragment)[];
  loading: boolean;
  participantFilter: number | null;
  meetingTypeFilter: MeetingType;
  hideEditingButtons?: boolean;
  canLoadMoreInFuture?: boolean;
  canLoadMoreInPast?: boolean;
  meetingDisplayedRange: {
    start: string;
    end: string;
  };
  isShowingWeekends: boolean;
  loadMore: "past" | "future" | null;
  fillerHeight: number;
  onChangeLoadMore: (loadMore: "past" | "future" | null) => void;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
  scrollToDay?: string;
  todayRef?: React.Ref<HTMLDivElement>;
};

const OverviewMeetingList = forwardRef<HTMLDivElement, Props>(
  (
    {
      meetings,
      loading,
      participantFilter,
      meetingTypeFilter,
      meetingDisplayedRange,
      hideEditingButtons = false,
      loadMore,
      isShowingWeekends,
      canLoadMoreInFuture,
      canLoadMoreInPast,
      fillerHeight,
      onChangeLoadMore,
      onClickMeetingLink,
      compact: isCompact = false,
      scrollToDay,
      todayRef: todayRefProp,
    },
    scrollTargetRef,
  ) => {
    const [now, setNow] = useState(moment().startOf("minute").format());
    const meetingsByStartDate: Record<
      string,
      (MeetingDialogFragmentFragment | MeetingsOverviewMeetingFragment)[]
    > = groupBy(meetings, (meeting) =>
      moment(meeting.startDatetime).format("YYYY-MM-DD"),
    );
    const dayCount =
      moment(meetingDisplayedRange.end)
        .startOf("day")
        .diff(moment(meetingDisplayedRange.start).startOf("day"), "days") + 1;
    const daysInRange = range(dayCount)
      .map((dayIndex) => {
        return moment(meetingDisplayedRange.start)
          .add(dayIndex, "days")
          .format("YYYY-MM-DD");
      })
      .filter((day) => {
        return isShowingWeekends || ![6, 7].includes(moment(day).isoWeekday());
      });

    const currentYear = moment().format("YYYY");

    useEffect(() => {
      const si = setInterval(() => {
        setNow(moment().startOf("minute").format());
      }, 2000);
      return function cleanup() {
        clearInterval(si);
      };
    }, []);

    const meetingRows = compact(
      daysInRange.map((date) => {
        const isToday = moment(date).isSame(moment(), "day");
        const isPastDay = moment(date).isBefore(moment(), "day");
        const dayMeetings = meetingsByStartDate[date]
          ? meetingsByStartDate[date]
              .filter((meeting) => {
                if (participantFilter === null) {
                  return true;
                }
                const participantIds = meeting.participants
                  ? assertEdgesNonNullWithStringId(meeting.participants).map(
                      (participantNode) => participantNode.user?.id,
                    )
                  : [];
                return participantIds.includes(participantFilter);
              })
              .filter((meeting) => {
                if (meetingTypeFilter === MeetingType.ALL) {
                  return true;
                }
                return (
                  (meeting.meetingGroup?.isFormalOneonone &&
                    meetingTypeFilter === MeetingType.ONE_ON_ONE) ||
                  (!meeting.meetingGroup?.isFormalOneonone &&
                    meetingTypeFilter === MeetingType.GROUP)
                );
              })
          : [];
        const pastMeetings = dayMeetings.filter((meeting) => {
          return meeting.endDatetime
            ? moment().isAfter(meeting.endDatetime)
            : false;
        });
        const futureMeetings = dayMeetings.filter((meeting) => {
          return meeting.endDatetime
            ? moment().isSameOrBefore(meeting.endDatetime)
            : false;
        });
        return dayMeetings.length === 0 ? null : isCompact ? (
          <div key={date}>
            <div key="meetings">
              <div className="flex flex-col gap-px">
                {pastMeetings.map((meeting) => (
                  <OverviewMeeting
                    key={meeting.id}
                    meeting={meeting}
                    hideEditingButtons={hideEditingButtons}
                    onClickMeetingLink={onClickMeetingLink}
                    compact={isCompact}
                    isPast
                    now={now}
                  />
                ))}
              </div>
              <div className="flex flex-col gap-px">
                {futureMeetings.map((meeting) => (
                  <OverviewMeeting
                    key={meeting.id}
                    meeting={meeting}
                    hideEditingButtons={hideEditingButtons}
                    onClickMeetingLink={onClickMeetingLink}
                    compact={isCompact}
                    now={now}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <MotionDiv
            key={date}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="py-4"
          >
            <AnimatePresence>
              {!isCompact && (
                <div className="flex items-center justify-between gap-1 tracking-tight px-2 mb-0.5">
                  <div
                    className={classNames(
                      "py-1 text-xs font-medium",
                      isToday
                        ? "text-blue-600"
                        : isPastDay
                          ? "text-gray-500"
                          : "text-gray-700",
                    )}
                    ref={(el) => {
                      if (isToday && todayRefProp) {
                        if (typeof todayRefProp === "function") {
                          todayRefProp(el);
                        } else {
                          (
                            todayRefProp as React.MutableRefObject<HTMLDivElement | null>
                          ).current = el;
                        }
                      }
                      const targetDay =
                        scrollToDay || moment().format("YYYY-MM-DD");
                      if (date === targetDay && scrollTargetRef) {
                        if (typeof scrollTargetRef === "function") {
                          scrollTargetRef(el);
                        } else {
                          (
                            scrollTargetRef as React.MutableRefObject<HTMLDivElement | null>
                          ).current = el;
                        }
                      }
                    }}
                    key="date"
                  >
                    {moment(date)
                      .format(
                        isToday
                          ? "ddd, MMM D, YYYY ([Today])"
                          : "ddd, MMM D, YYYY",
                      )
                      .replace(`, ${currentYear}`, "")}
                  </div>
                  {dayMeetings.length === 0 && (
                    <div className="text-gray-300 text-xs">No meetings</div>
                  )}
                </div>
              )}
              {dayMeetings.length > 0 && (
                <div key="meetings">
                  <div className="flex flex-col gap-px">
                    {pastMeetings.map((meeting) => {
                      return (
                        <OverviewMeeting
                          key={meeting.id}
                          meeting={meeting}
                          hideEditingButtons={hideEditingButtons}
                          onClickMeetingLink={onClickMeetingLink}
                          compact={isCompact}
                          isPast
                          now={now}
                        />
                      );
                    })}
                  </div>
                  <div className="flex flex-col gap-px">
                    {futureMeetings.map((meeting) => {
                      return (
                        <OverviewMeeting
                          key={meeting.id}
                          meeting={meeting}
                          hideEditingButtons={hideEditingButtons}
                          onClickMeetingLink={onClickMeetingLink}
                          compact={isCompact}
                          now={now}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </AnimatePresence>
          </MotionDiv>
        );
      }),
    );

    return (
      <>
        {!isCompact && (
          <div className="px-2">
            <Button
              size={ButtonSize.medium}
              theme={buttonTheme.default}
              fullWidth
              className="justify-center text-center bg-transparent"
              disabled={loading || !canLoadMoreInPast}
              onClick={() => {
                onChangeLoadMore("past");
              }}
              text={
                loadMore === "past"
                  ? undefined
                  : canLoadMoreInPast
                    ? "Load Previous"
                    : "No More Meetings."
              }
            >
              {loadMore === "past" && <Loading size={4} />}
            </Button>
          </div>
        )}
        {meetingRows.length === 0 && (
          <div className="text-gray-400 text-center py-1 italic rounded-md my-6">
            No meetings
          </div>
        )}
        <div className={classNames("flex flex-col", isCompact && "divide-y")}>
          {isCompact ? (
            meetingRows
          ) : (
            <AnimatePresence mode="popLayout">
              {/* Interleave inset dividers between days so the lines match the
                  other meeting-panel dividers (mx-5) without shifting the
                  full-width rows. */}
              {meetingRows.flatMap((row, i) =>
                i === 0
                  ? [row]
                  : [
                      <div
                        key={`meeting-day-divider-${i}`}
                        className="border-t border-gray-200 mx-5"
                      />,
                      row,
                    ],
              )}
            </AnimatePresence>
          )}
        </div>
        {!isCompact && (
          <div className="px-2">
            <Button
              size={ButtonSize.medium}
              theme={buttonTheme.default}
              fullWidth
              className="justify-center text-center bg-transparent"
              disabled={loading || !canLoadMoreInFuture}
              onClick={() => {
                onChangeLoadMore("future");
              }}
              text={
                loadMore === "future"
                  ? undefined
                  : canLoadMoreInFuture
                    ? "Load More"
                    : "No More Meetings."
              }
            >
              {loadMore === "future" && <Loading size={4} />}
            </Button>
          </div>
        )}
        {!isCompact && (
          <div
            className="flex-1"
            id="filler"
            style={{ minHeight: fillerHeight }}
          />
        )}
      </>
    );
  },
);

export default OverviewMeetingList;
